import  { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import styled from 'styled-components';
import useAuth from '../../../../features/hooks/useAuth';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useUpdateassetReqMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import {  useUpdateMultipleDocsMutation } from '../../../../features/addassets/addassetfinalApiSlice';
import { CommonButton } from '../../../../Styles/CommonStyles';
import nameStringFn from '../../../../data/nameStringFn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import { useAddNewTransactionMutation } from '../../../../features/transactions/transactionsApiSlice';

const Container = styled.div`
  margin-left: 10px;
  margin-right: 10px;
`;

const ButtonCont = styled.div`
  display: flex;
  margin-top: 10px;
  margin-bottom: 40px;
  gap: 15px;
`;

const AcceptButton = styled(CommonButton)`
  background-color: #4caf50;
  
  &:hover {
    background-color: #45a049;
  }
`;

const RejectButton = styled(CommonButton)`
  background-color: #f44336;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const Bo_StoreReturnInboxForm = ({ row, showActionOptions, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  // Get logged in user data
  const { id, email, designation, designationSuffix, numSuffix, roles } = useAuth();
  const user_current_office = useSelector((state) => state.office.off);

  // Get user data
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({ getSingleuser: data?.entities }),
  });

  // Update request mutation
  const [updateRequest] = useUpdateassetReqMutation();
  
  // Asset and user update mutations
  const [updateMultipleAssetDocs] = useUpdateMultipleDocsMutation();
  const [addTrans] = useAddNewTransactionMutation();


  // Handle accepting the assets into the store
  const handleAccept = async () => {
    if (!remark.trim()) {
      alert("Please provide a remark");
      return;
    }
    
    if (!row) {
      alert("Request data not found");
      return;
    }
    
    try {
      setProcessing(true);
      
      // Get asset IDs from the request
      const assetIds = row.assets_requested?.[row?.assets_requested?.length - 1]?.data || [];
      
      if (assetIds.length === 0) {
        setProcessing(false);
        return alert("No assets found in this request");
      }

      // Create a transaction record
      const transacData = {
        transac_id: row?.uniqueReqId,
        req_id: row.id,
        asset_id: assetIds,
        transac_type: 'BoToStoreReturn',
        indi_asset: false,
        from_userid: getSingleuser?.[row.req_creator_id]?.email,
        from_office: row?.req_data?.[0]?.sent_by?.office || user_current_office,
        from_desig: row?.req_data?.[0]?.sent_by?.designation || 'BO',
        from_store: row?.req_store,
        to_userid: email,
        to_office: user_current_office,
        to_desig: designation?.[user_current_office],
        to_store: row.req_store,
        pdfPath: "",
        time_stamp: new Date().toISOString()
      };
      
      // Update assets to be in the store
      const assetBody = {
        assetIdArray: assetIds,
        updates: {
          cur_loc: "store",
          alloc_user: "",
          alloc_ofc: "Not Applicable",
          cur_user_name: `User not Defined`,
          last_trans_date: new Date().toISOString()
        }
      };
      
      // Update request status
      const senttoData = {
        sent_to_tag: "botostore_return_req_accepted",
        action: 'BO Asset Return Request Accepted by Store',
        Text: remark,
        sent_by: { 
          id, 
          name: getSingleuser?.[id]?.name || 'Store Officer', 
          office: user_current_office, 
          designation: designation[user_current_office], 
          designationSuffix: designationSuffix[user_current_office], 
          numSuffix: numSuffix[user_current_office] 
        },
        sent_to: { 
          id: row.req_creator_id,
          name: getSingleuser?.[row.req_creator_id]?.name || 'Branch Officer',
          office: row?.req_data?.[0]?.sent_by?.office || user_current_office,
          designation: row?.req_data?.[0]?.sent_by?.designation || 'BO'
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row.req_creator_id])}-${row.req_data[0]?.sent_by?.office}-${row.req_data[0]?.sent_by?.designation || 'Unknown'}`,
        curr_status: "Closed",
        time_stamp: new Date()
      };
      
      const body = { id: row.id, senttoData: senttoData };

      // Execute all updates
      await updateRequest(body);
      await updateMultipleAssetDocs(assetBody);
      await addTrans(transacData);

      setProcessing(false);
      alert('Assets successfully accepted into store');
      handleBack();
    } catch (error) {
      setProcessing(false);
      console.error("Error accepting assets:", error);
      alert(`Error: ${error.message || 'Failed to accept assets'}`);
    }
  };
  
  // Handle rejecting the request
  const handleReject = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }
    
    if (!row) {
      alert("Request data not found");
      return;
    }
    
    try {
      setProcessing(true);
      
      const senttoData = {
        sent_to_tag: "botostore_return_req_rejected",
        Text: remark,
        sent_by: { 
          id, 
          name: getSingleuser?.[id]?.name || 'Store Officer', 
          office: user_current_office, 
          designation: designation[user_current_office], 
          designationSuffix: designationSuffix[user_current_office], 
          numSuffix: numSuffix[user_current_office] 
        },
        sent_to: { 
          id: row.req_creator_id,
          name: getSingleuser?.[row.req_creator_id]?.name || 'Branch Officer',
          office: row?.req_data?.[0]?.sent_by?.office || user_current_office,
          designation: row?.req_data?.[0]?.sent_by?.designation || 'BO'
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row.req_creator_id])}-${row?.req_data?.[0]?.sent_by?.office}-${row?.req_data?.[0]?.sent_by?.designation || 'Unknown'}`,
        action: 'BO Asset Return Request Rejected by Store',
        time_stamp: new Date(),
        curr_status: 'Rejected'
      };

      const body = { id: row.id, senttoData: senttoData };

      await updateRequest(body);

      setProcessing(false);
      handleBack();
      alert('Return request rejected');
    } catch (error) {
      setProcessing(false);
      console.error("Error rejecting request:", error);
      alert(`Error: ${error.message || 'Failed to reject request'}`);
    }
  };
console.log(showActionOptions)
  // Check if this request is meant for the current user (Store approver)
  const isRequestForCurrentUser = row?.curr_sent_to === id;

  useEffect(() => {
    if (row && !isRequestForCurrentUser) {
      navigate('/management/assets/myrequests/inbox');
      alert('This request is not assigned to you');
    }
  }, [row, isRequestForCurrentUser, navigate]);

  if (!row) return <div>Request not found</div>;
  if (!isRequestForCurrentUser) return null;

  return (
    <Container>
      {showActionOptions ? (
        <>
          <RemarkContainer>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Add Remark"
              variant="outlined"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </RemarkContainer>
          
          <ButtonCont>
            <AcceptButton 
              onClick={handleAccept} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Accept Returned Assets into Store'}
            </AcceptButton>
            <RejectButton 
              onClick={handleReject} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Reject Return Request'}
            </RejectButton>
          </ButtonCont>
        </>
      ) : null}
    </Container>
  );
};

export default Bo_StoreReturnInboxForm; 